import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Button
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link } from 'react-router-dom';
import { Calculate, TrendingUp, Public, Security } from '@mui/icons-material';
import { getRolesByRegion, ROLE_REGIONS } from '../../data/roles';
import { formatCurrency } from '../../utils/formatCurrency';
import Layout from '../../components/Layout';

export default function MethodologyPage() {
  const ukRoles = getRolesByRegion(ROLE_REGIONS.UK);
  const usRoles = getRolesByRegion(ROLE_REGIONS.US);

  return (
    <Layout variant="default">
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 'auto' }, maxWidth: '100%', overflowX: 'hidden' }}>
        {/* Hero Section */}
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 900,
              color: 'text.primary',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            How cost.wtf Works
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 600,
              maxWidth: 800,
              mx: 'auto',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              px: { xs: 2, sm: 0 },
            }}
          >
            Complete transparency into our meeting cost calculations, 
            role definitions, and salary methodology
          </Typography>
        </Box>

        {/* Overview Section */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Calculate sx={{ color: 'text.primary' }} fontSize="large" />
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              What We Calculate
            </Typography>
          </Box>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            cost.wtf calculates the <strong>true cost</strong> of meetings by factoring in 
            not just base salaries, but the complete cost of employment including National Insurance, 
            pension contributions, office overheads, and other employer costs.
          </Typography>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Key Principle:</strong> We use "fully-loaded" employment costs, 
              which are typically 1.4-1.6x the base salary when you include all employer 
              expenses. This gives a realistic picture of what meetings actually cost organizations.
            </Typography>
          </Alert>
        </Paper>

        {/* Role Definitions */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom color="text.primary">
            Role Definitions & Salary Bands
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            Our salary data is based on market research from multiple sources including 
            Glassdoor, PayScale, industry surveys, and recruitment data from both regions.
          </Typography>

          <Box mb={4}>
            <Typography variant="h5" gutterBottom color="text.primary">
              UK vs US Regional Differences
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              All rates shown are hourly equivalents based on typical annual salaries. 
              US rates reflect higher base salaries and different employment cost structures.
            </Typography>
          </Box>

          {/* Mobile Card Layout - shows on screens smaller than 600px */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            {ukRoles.map((role) => {
              const usRole = usRoles.find(r => r.key === role.key);
              const responsibilities = getRoleResponsibilities(role.key);
              
              return (
                <Paper key={role.key} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography fontWeight="medium" gutterBottom sx={{ fontSize: '0.875rem' }}>
                    {role.label}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip 
                      label={`UK: ${formatCurrency(role.hourlyRate, 'GBP')}`}
                      sx={{ 
                        fontSize: '0.75rem',
                        bgcolor: 'grey.600',
                        color: 'white',
                        '&.MuiChip-root': {
                          bgcolor: 'grey.600',
                          color: 'white'
                        }
                      }}
                      size="small"
                    />
                    <Chip 
                      label={`US: ${formatCurrency(usRole.hourlyRate, 'USD')}`}
                      sx={{ 
                        fontSize: '0.75rem',
                        bgcolor: 'grey.600',
                        color: 'white',
                        '&.MuiChip-root': {
                          bgcolor: 'grey.600',
                          color: 'white'
                        }
                      }}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                    {responsibilities}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          {/* Table Layout - shows on screens 600px and wider */}
          <TableContainer 
            component={Paper} 
            variant="outlined" 
            sx={{ 
              overflowX: 'auto', 
              maxWidth: '100%',
              display: { xs: 'none', sm: 'block' }
            }}
          >
            <Table sx={{ minWidth: { xs: 'auto', md: 'auto' }, width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell><strong>UK Rate</strong></TableCell>
                  <TableCell><strong>US Rate</strong></TableCell>
                  <TableCell><strong>Typical Responsibilities</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ukRoles.map((role) => {
                  const usRole = usRoles.find(r => r.key === role.key);
                  const responsibilities = getRoleResponsibilities(role.key);
                  
                  return (
                    <TableRow key={role.key}>
                      <TableCell>
                        <Typography fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                          {role.label}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={formatCurrency(role.hourlyRate, 'GBP')} 
                          size="small"
                          sx={{ 
                            bgcolor: 'grey.600',
                            color: 'white',
                            '&.MuiChip-root': {
                              bgcolor: 'grey.600',
                              color: 'white'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={formatCurrency(usRole.hourlyRate, 'USD')} 
                          size="small"
                          sx={{ 
                            bgcolor: 'grey.600',
                            color: 'white',
                            '&.MuiChip-root': {
                              bgcolor: 'grey.600',
                              color: 'white'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                          {responsibilities}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* True Cost Breakdown */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <TrendingUp sx={{ color: 'text.primary' }} fontSize="large" />
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              True Cost Calculation
            </Typography>
          </Box>

          <Typography variant="body1" paragraph>
            We don't just use base salaries. Here's how we calculate the real cost of employment:
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2, width: '100%', maxWidth: '100%' }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  Base Employment Costs
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    <strong>Base Salary:</strong> Market-rate annual salary
                  </Typography>
                  <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    <strong>National Insurance:</strong> 13.8% employer contribution (UK)
                  </Typography>
                  <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    <strong>Pension Contributions:</strong> 3-8% typical employer match
                  </Typography>
                  <Typography component="li" variant="body2" color="text.primary">
                    <strong>Payroll Taxes:</strong> Regional variations (US: FICA, state taxes)
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  Additional Overheads
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    <strong>Office Space:</strong> Rent, utilities, facilities per person
                  </Typography>
                  <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    <strong>Equipment & IT:</strong> Laptops, software licenses, phone
                  </Typography>
                  <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    <strong>Training & Development:</strong> Professional development budget
                  </Typography>
                  <Typography component="li" variant="body2" color="text.primary">
                    <strong>HR & Admin:</strong> Recruitment, onboarding, management overhead
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Total Multiplier:</strong> Our calculations apply a 1.4-1.6x multiplier 
              to base salaries, which aligns with standard industry fully-loaded cost calculations 
              used by finance and HR departments.
            </Typography>
          </Alert>
        </Paper>

        {/* Meeting Cost Formula */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom color="text.primary">
            Meeting Cost Formula
          </Typography>
          
          <Box sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom color="text.primary" sx={{ fontFamily: 'monospace', fontSize: { xs: '1rem', sm: '1.25rem' }, overflowWrap: 'break-word' }}>
              Total Cost = Σ (Attendee Count × True Hourly Rate × Total Hours)
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Where <strong>Total Hours</strong> = Meeting Duration + Preparation Time
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom color="text.primary">
              Example Calculation:
            </Typography>
            <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', bgcolor: 'background.paper', color: 'text.primary', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', overflowX: 'auto', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              1 Senior Manager (£130/hr) + 2 Product Managers (£100/hr each)<br/>
              Meeting: 60 minutes + Preparation: 30 minutes = 1.5 hours total<br/><br/>
              
              Calculation:<br/>
              • Senior Manager: 1 × £130 × 1.4 multiplier × 1.5 hours = £273<br/>
              • Product Managers: 2 × £100 × 1.4 multiplier × 1.5 hours = £420<br/>
              • <strong>Total: £693</strong>
            </Typography>
          </Box>
        </Paper>

        {/* Document Cost Formula */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom color="text.primary">
            Document Cost Formula
          </Typography>
          
          <Typography variant="body1" paragraph>
            The Document Calculator tracks the full lifecycle cost of creating business documents, 
            from initial writing through review cycles to final reading by stakeholders.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2, mb: 3, width: '100%', maxWidth: '100%' }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  📝 Writing Phase
                </Typography>
                <Typography variant="body2" paragraph>
                  Time estimates based on document type and complexity:
                </Typography>
                <Box component="ul" sx={{ pl: 2, fontSize: '0.875rem' }}>
                  <Typography component="li" variant="body2" color="text.primary">Simple: 15 words/min</Typography>
                  <Typography component="li" variant="body2" color="text.primary">Technical: 8 words/min</Typography>
                  <Typography component="li" variant="body2" color="text.primary">Research: 7 words/min</Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  🔍 Review Phase
                </Typography>
                <Typography variant="body2" paragraph>
                  Review time calculated as percentage of writing time:
                </Typography>
                <Box component="ul" sx={{ pl: 2, fontSize: '0.875rem' }}>
                  <Typography component="li" variant="body2" color="text.primary">Standard review: 40% of writing time</Typography>
                  <Typography component="li" variant="body2" color="text.primary">Multiple review cycles supported</Typography>
                  <Typography component="li" variant="body2" color="text.primary">Complexity adjustments applied</Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  📖 Reading Phase
                </Typography>
                <Typography variant="body2" paragraph>
                  Reading time based on average reading speed:
                </Typography>
                <Box component="ul" sx={{ pl: 2, fontSize: '0.875rem' }}>
                  <Typography component="li" variant="body2" color="text.primary">Average: 200 words/minute</Typography>
                  <Typography component="li" variant="body2" color="text.primary">Minimum: 6 minutes</Typography>
                  <Typography component="li" variant="body2" color="text.primary">All stakeholders counted</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom color="text.primary" sx={{ fontFamily: 'monospace', fontSize: { xs: '1rem', sm: '1.25rem' }, overflowWrap: 'break-word' }}>
              Total Cost = Writing Cost + Review Cost + Reading Cost
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, overflowWrap: 'break-word' }}>
              Where each phase cost = Role Count × Hourly Rate × Time Estimate × 1.4 multiplier
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom color="text.primary">
              Example Document Calculation:
            </Typography>
            <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', bgcolor: 'background.paper', color: 'text.primary', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', overflowX: 'auto', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Technical specification: 2,000 words<br/>
              Author: 1 Senior Engineer (£100/hr) - 11 hours writing<br/>
              Reviewers: 1 Engineering Manager (£120/hr) - 4.4 hours review<br/>
              Readers: 5 Product Managers (£100/hr each) - 0.17 hours reading<br/><br/>
              
              Calculation:<br/>
              • Writing: 1 × £100 × 1.4 × 11 hours = £1,540<br/>
              • Review: 1 × £120 × 1.4 × 4.4 hours = £739<br/>
              • Reading: 5 × £100 × 1.4 × 0.17 hours = £119<br/>
              • <strong>Total: £2,398</strong>
            </Typography>
          </Box>
        </Paper>

        {/* Data Sources */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Public sx={{ color: 'text.primary' }} fontSize="large" />
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              Data Sources & Methodology
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom color="text.primary">
                Salary Research Sources
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Glassdoor salary database (2023-2024 data)
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  PayScale compensation reports
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Robert Half salary guides
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Tech salary surveys (Stack Overflow, etc.)
                </Typography>
                <Typography component="li" variant="body2" color="text.primary">
                  Recruitment agency data and job postings
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom color="text.primary">
                Cost Multiplier Research
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  HMRC employer cost guidance (UK)
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  BLS employer costs survey (US)
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Industry HR benchmarking studies
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Office space cost per employee data
                </Typography>
                <Typography component="li" variant="body2" color="text.primary">
                  IT equipment and software licensing costs
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Transparency & Updates */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Security sx={{ color: 'text.primary' }} fontSize="large" />
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              Transparency & Updates
            </Typography>
          </Box>

          <Typography variant="body1" paragraph>
            We believe in full transparency about our methodology. If you think our rates 
            are off or have better data sources, we'd love to hear from you.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2, width: '100%', maxWidth: '100%' }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom color="text.primary">
                Our Commitments
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Regular salary data reviews (quarterly)
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Open-source methodology documentation
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Regional adjustments based on cost-of-living
                </Typography>
                <Typography component="li" variant="body2" color="text.primary">
                  Industry-specific role variations coming soon
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom color="text.primary">
                Limitations & Disclaimers
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Estimates based on market averages
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Regional variations within countries not captured
                </Typography>
                <Typography component="li" variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Industry-specific premiums not yet included
                </Typography>
                <Typography component="li" variant="body2" color="text.primary">
                  Startup vs enterprise differences simplified
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Call to Action */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            textAlign: 'center',
            background: theme => theme.palette.mode === 'dark' 
              ? 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)'
              : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            color: 'white',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
            Ready to Calculate Your Meeting Costs?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Now that you understand the methodology, try our calculators with confidence.
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, justifyContent: 'center', flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
            <Button
              component={Link}
              to="/meetings"
              variant="contained"
              size="large"
              sx={{ 
                bgcolor: 'common.white', 
                color: 'primary.main',
                '&:hover': { 
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.200' : 'grey.100' 
                },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Meeting Calculator
            </Button>
            <Button
              component={Link}
              to="/live-meetings"
              variant="contained"
              size="large"
              sx={{ 
                bgcolor: 'common.white', 
                color: 'primary.main',
                '&:hover': { 
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.200' : 'grey.100' 
                },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Live Timer
            </Button>
            <Button
              component={Link}
              to="/documents"
              variant="contained"
              size="large"
              sx={{ 
                bgcolor: 'common.white', 
                color: 'primary.main',
                '&:hover': { 
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'grey.200' : 'grey.100' 
                },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Document Calculator
            </Button>
          </Box>
        </Paper>
      </Container>
    </Layout>
  );
}

// Helper function for role responsibilities
function getRoleResponsibilities(roleKey) {
  const responsibilities = {
    seniorManager: "Strategic planning, team leadership, budget oversight, stakeholder management",
    productManager: "Product strategy, roadmap planning, feature prioritization, cross-team coordination", 
    productDesigner: "UX/UI design, user research, prototyping, design systems",
    dataAnalyst: "Data analysis, reporting, insights generation, dashboard creation",
    engineeringManager: "Technical team leadership, architecture decisions, code review, mentoring",
    deliveryManager: "Project management, sprint planning, delivery coordination, process optimization",
    seniorSoftwareEngineer: "Senior development, system design, mentoring junior developers, technical leadership",
    juniorSoftwareEngineer: "Software development, testing, learning, contributing to team projects"
  };
  
  return responsibilities[roleKey] || "Role-specific responsibilities and expertise";
}